import { useAnalytics } from "@codat/analytics";
import {
  Button,
  CircleEmptyAlertIcon,
  RightArrowIcon,
  Typography,
  useToast,
} from "@codat/orchard-ui";
import { GetCompanyResponse } from "api/public";
import { useGetLinkURL } from "hooks/query/useGetLinkURL";
import pluralize from "pluralize";
import { useUserState } from "portal-ui-remote/UserStateContext";
import React, { useMemo, useState } from "react";
import formatUrl from "utils/formatUrl";

import { paths } from "components/App/paths";
import ClipboardInput from "components/ClipboardInput";
import EmailTemplateAccordion from "components/EmailTemplateAccordion";
import Modal from "components/Modal";

import { Connection } from "./Connection";
import styles from "./LinkModal.module.scss";

export interface LinkModalProps {
  company: GetCompanyResponse;
  onClose: () => void;
}

const LinkModal: React.FC<LinkModalProps> = ({ company, onClose }) => {
  const { addToast } = useToast();
  const { sendEvent } = useAnalytics();
  const { userState } = useUserState();
  const { data: linkUrl } = useGetLinkURL({
    redirect: company.redirect,
    userId: userState?.userId,
    clientId: userState?.id,
  });
  const [visibleLinkUrlId, setVisibleLinkUrlId] = useState<string | null>(null);
  const isSpendInsightsCompany = company.products.includes(
    "supplier-enablement"
  );
  const hasConnections = !!company.dataConnections.length;
  const renderEmailTemplate = isSpendInsightsCompany && company && userState;

  const onCopy = () => {
    sendEvent("Copy Link URL Button - Click");
    addToast({ message: "Copied link URL to clipboard", variant: "success" });
  };

  const handleClose = () => {
    setVisibleLinkUrlId(null);
    onClose();
  };

  const connectionErrorsCount = useMemo(
    () =>
      company.dataConnections.reduce((acc, dataConnection) => {
        const connectionErrorsCount =
          dataConnection?.dataConnectionErrors?.reduce(
            (connectionAcc) => connectionAcc + 1,
            0
          ) ?? 0;

        return acc + connectionErrorsCount;
      }, 0) ?? 0,
    [company.dataConnections]
  );

  return (
    <>
      <Modal
        onClose={handleClose}
        className={styles.linkModal}
        eventProperties={{ companyId: company.id }}
        data-testid="link-modal"
        size="medium"
      >
        <>
          <Modal.Header
            title="Onboarding"
            overline={company.name}
            onClose={handleClose}
          />
          <Modal.Body variant="dark">
            <Modal.Section
              title="Link URL"
              subtitle={`Share this URL with ${company.name} so they can grant you access to their financial data.`}
              variant="card"
            >
              <ClipboardInput
                label="Link URL to share with companies"
                aria-describedby="link-url-label-text"
                text={linkUrl}
                id="link-modal-link"
                data-testid="link-modal-link"
                onCopy={onCopy}
                isSkeleton={!linkUrl}
                inputName="Link URL"
              />
            </Modal.Section>
            {renderEmailTemplate && (
              <Modal.Section
                title="Email template"
                subtitle={`Copy this template and paste it in an email to ${company.name} to invite them to share their financial information.`}
                variant="card"
              >
                <EmailTemplateAccordion
                  linkUrl={linkUrl}
                  companyName={company.name}
                  clientName={userState.clientName}
                  clientId={userState.id}
                />
              </Modal.Section>
            )}
            {hasConnections && (
              <>
                <Modal.Section title="Connections" variant="card">
                  <ul className={styles.connectionsList}>
                    {company.dataConnections?.map((connection) => (
                      <Connection
                        key={connection.id}
                        connection={connection}
                        companyId={company.id}
                        showLinkUrl={visibleLinkUrlId === connection.id}
                        onToggleLinkUrl={(id) =>
                          setVisibleLinkUrlId(
                            id === visibleLinkUrlId ? null : id
                          )
                        }
                      />
                    ))}
                  </ul>
                </Modal.Section>
                {connectionErrorsCount > 0 && (
                  <Modal.Section variant="card">
                    <div className={styles.errorsWrapper}>
                      <CircleEmptyAlertIcon />
                      <Typography
                        variant="heading_sm"
                        as="div"
                        noMargin
                        data-testid="errors-summary"
                      >{`${
                        company.name
                      } has ${connectionErrorsCount} connection ${pluralize(
                        "error",
                        connectionErrorsCount
                      )}`}</Typography>

                      <Button
                        label="View errors"
                        color="interface"
                        variant="secondary"
                        to={`${formatUrl(paths.companies.CONNECTIONS, {
                          companyId: company.id,
                        })}#connection-errors`}
                        className={styles.viewErrorsButton}
                        data-testid="link-modal-view-errors"
                      >
                        <span className={styles.label}>View errors</span>
                        <RightArrowIcon />
                      </Button>
                    </div>
                  </Modal.Section>
                )}
              </>
            )}
          </Modal.Body>
        </>
      </Modal>
    </>
  );
};

export default LinkModal;
